import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, map, shareReplay } from 'rxjs/operators';
import { KEYCLOAK_ROLES } from '../../shared/constants/auth.constants';
import { ServerListViewWithKey, UserListViewsService } from '../../shared/services/user-list-views.service';
import { saveListFilters } from '../../shared/utils/filters';
import { viewFromServer } from '../../shared/utils/list-views';
import { VIEW_QUERY_PARAM } from '../../shared/utils/list-views-state';
import { VIEW_SCREENS, ViewScreen } from './command-palette.views';
import { NavigationService } from '../services/navigation.service';
import { PermissionService } from '../services/permission.service';
import { COMMAND_ACTIONS } from './command-palette.actions';
import { MatchRange, SearchGroup, SearchItem } from './command-palette.model';
import { adDetail, invoiceDetail, orderDetail, productDetail, relativeTime } from './command-palette.preview';
import { providerLabel } from '../../shared/utils/import-per-channel';

import { ProductsService } from '../../features/products/services/products.service';
import { Product } from '../../features/products/models/product.model';
import { AdvertisementsService } from '../../features/advertisements/services/advertisements.service';
import { AdvertisementListItem } from '../../features/advertisements/models/advertisement.model';
import { CategoriesService } from '../../features/categories/services/categories.service';
import { Category } from '../../features/categories/models/category.model';
import { VariationsService } from '../../features/variations/services/variations.service';
import { Variation } from '../../features/variations/models/variation.model';
import { IntegrationsService } from '../../features/integrations/services/integrations.service';
import { Integration } from '../../features/integrations/models/integration.model';
import { WebhookDestinationsService } from '../../features/webhook-destinations/services/webhook-destinations.service';
import { WebhookDestination } from '../../features/webhook-destinations/models/webhook-destination.model';
import { CompaniesService } from '../../features/companies/services/companies.service';
import { Company } from '../../features/companies/models/company.model';
import { CompanyGroupsService } from '../../features/company-groups/services/company-groups.service';
import { GetCompanyGroupsResult } from '../../features/company-groups/models/company-groups.result';
import { InvoicesService } from '../../features/invoices/services/invoices.service';
import { InvoiceListItem } from '../../features/invoices/models/invoice.model';
import { OrdersService } from '../../features/orders/services/orders.service';
import { OrderListItem } from '../../features/orders/models/order.model';

// Formato antigo (só os 5 últimos): lido uma vez para semear o histórico de uso.
const RECENTS_KEY = 'mc-command-palette-recents';
const USAGE_KEY = 'mc-command-palette-usage';
// Quantos itens o histórico de uso guarda (os menos usados saem primeiro).
const USAGE_MAX = 60;
const MOST_USED_MAX = 5;
const ENTITY_RESULTS_MAX = 6;
// Favoritos da busca moram nas preferências do usuário no servidor, no mesmo lugar das visões
// salvas (por usuário e empresa): tela "busca", nome "favoritos".
const FAVORITES_LIST_KEY = 'busca';
const FAVORITES_NAME = 'favoritos';
const FAVORITES_MAX = 30;

interface UsageEntry {
  item: SearchItem;
  count: number;
  last: number;
}

// Uma FONTE de entidade pesquisável (produtos, categorias, empresas…). load() traz a lista (pouca
// coisa nas de config; catálogo/anúncios são cacheados). toItem mapeia pro SearchItem com deep-link.
interface EntitySource {
  label: string;
  roles: string[];
  load: () => Observable<unknown[]>;
  toItem: (row: unknown) => SearchItem;
}

// Labels de TODOS os grupos assíncronos — o componente remove estes antes de mesclar a resposta
// nova (client + server). Ao adicionar uma fonte, seu label entra aqui automaticamente (derivado).
export const DYNAMIC_GROUP_LABELS = [
  'Produtos',
  'Anúncios',
  'Categorias',
  'Variações',
  'Integrações',
  'Destinos de webhook',
  'Empresas',
  'Grupos de empresas',
  'Notas fiscais',
  'Pedidos',
  'Visões',
];

// Cérebro do command palette: agrega páginas + ações + TODAS as entidades pesquisáveis, com scoring
// de relevância e filtro por permissão efetiva (PermissionService). Recentes em localStorage.
@Injectable({ providedIn: 'root' })
export class CommandPaletteService {
  private readonly permissions = inject(PermissionService);
  private readonly navigation = inject(NavigationService);

  private readonly products = inject(ProductsService);
  private readonly advertisements = inject(AdvertisementsService);
  private readonly categories = inject(CategoriesService);
  private readonly variations = inject(VariationsService);
  private readonly integrations = inject(IntegrationsService);
  private readonly webhookDestinations = inject(WebhookDestinationsService);
  private readonly companies = inject(CompaniesService);
  private readonly companyGroups = inject(CompanyGroupsService);
  private readonly invoices = inject(InvoicesService);
  private readonly orders = inject(OrdersService);
  private readonly listViews = inject(UserListViewsService);

  // Itens já mapeados, cacheados por label (uma carga por sessão). invalidate() força recarga.
  private readonly cache = new Map<string, Observable<SearchItem[]>>();

  // Visões salvas do usuário (todas as telas), recarregadas a cada abertura da busca: a pessoa
  // pode ter salvo uma visão agora há pouco.
  private userViews$: Observable<ServerListViewWithKey[]> = of([]);
  // Recorte de cada visão listada, pelo id do item: abrir uma visão grava o recorte antes de navegar.
  private readonly viewFilters = new Map<string, { screenKey: string; filters: Record<string, unknown> }>();

  private favorites: SearchItem[] = [];
  /** Emite quando os favoritos chegam do servidor ou mudam. */
  readonly favoritesChanged = new Subject<void>();
  // Gravações em fila: cada uma manda a lista inteira, e fora de ordem a última a chegar venceria.
  private readonly favoritesSave = new Subject<SearchItem[]>();

  constructor() {
    this.favoritesSave
      .pipe(
        concatMap((items) =>
          this.listViews.upsertRaw(FAVORITES_LIST_KEY, FAVORITES_NAME, { items }).pipe(catchError(() => of(undefined))),
        ),
      )
      .subscribe();
  }

  // ── Fontes de entidade (client-side: carrega a lista e filtra na memória) ─────────────────────
  private entitySources(): EntitySource[] {
    return [
      {
        label: 'Categorias',
        roles: [KEYCLOAK_ROLES.CATEGORY],
        load: () => this.categories.list(),
        toItem: (r) => {
          const c = r as Category;
          return { id: `category:${c.id}`, type: 'product', title: c.name, icon: 'ti ti-category', route: `/categorias/editar/${c.id}` };
        },
      },
      {
        label: 'Variações',
        roles: [KEYCLOAK_ROLES.VARIATION],
        load: () => this.variations.list(),
        toItem: (r) => {
          const v = r as Variation;
          return { id: `variation:${v.id}`, type: 'product', title: v.name, icon: 'ti ti-adjustments-horizontal', route: `/variacoes/editar/${v.id}` };
        },
      },
      {
        label: 'Integrações',
        roles: [KEYCLOAK_ROLES.INTEGRATION],
        load: () => this.integrations.getByCompany(),
        toItem: (r) => {
          const i = r as Integration;
          return {
            id: `integration:${i.id}`,
            type: 'product',
            title: String(i.provider),
            subtitle: 'Integração de marketplace',
            icon: 'ti ti-plug-connected',
            route: `/integracoes/editar/${i.id}`,
          };
        },
      },
      {
        label: 'Destinos de webhook',
        roles: [KEYCLOAK_ROLES.WEBHOOK_DESTINATION],
        load: () => this.webhookDestinations.list(),
        toItem: (r) => {
          const w = r as WebhookDestination;
          return { id: `webhook:${w.id}`, type: 'product', title: w.name, subtitle: w.url, icon: 'ti ti-webhook', route: `/destinos-webhook/editar/${w.id}` };
        },
      },
      {
        label: 'Empresas',
        roles: [KEYCLOAK_ROLES.COMPANY],
        load: () => this.companies.getAll(),
        toItem: (r) => {
          const c = r as Company;
          return { id: `company:${c.id}`, type: 'product', title: c.name, icon: 'ti ti-building', route: `/empresas/editar/${c.id}` };
        },
      },
      {
        label: 'Grupos de empresas',
        roles: [KEYCLOAK_ROLES.COMPANY_GROUP],
        load: () => this.companyGroups.list(),
        toItem: (r) => {
          const g = r as GetCompanyGroupsResult;
          return { id: `group:${g.id}`, type: 'product', title: g.name, icon: 'ti ti-building-community', route: `/grupos-empresas/editar/${g.id}` };
        },
      },
    ];
  }

  // ── Páginas + ações (síncrono, filtrado por permissão) ────────────────────────────────────────
  private pageItems(): SearchItem[] {
    return this.navigation
      .getSearchableItems()
      .flatMap((section) => section.items)
      .map((item) => ({ id: `page:${item.url}`, type: 'page', kind: 'page', title: item.title, subtitle: item.url, icon: item.icon, route: item.url }));
  }

  private actionItems(): (SearchItem & { keywords?: string })[] {
    return COMMAND_ACTIONS.filter((a) => this.hasRoles(a.roles)).map((a) => ({
      id: `action:${a.id}`,
      type: 'action',
      kind: 'page',
      title: a.title,
      subtitle: a.subtitle,
      icon: a.icon,
      route: a.route,
      keywords: a.keywords,
    }));
  }

  // Estado vazio: favoritos (pílulas) + abertos recentemente + ações (grade). "Ir para" só para quem
  // ainda não tem histórico nem favorito. Entidades entram na busca (com query).
  buildStatic(query: string): SearchGroup[] {
    const q = query.trim();
    if (!q) {
      const groups: SearchGroup[] = [];
      if (this.favorites.length) groups.push({ label: 'Favoritos', items: this.favorites, rank: 1100, layout: 'chips' });
      const favoriteIds = new Set(this.favorites.map((f) => f.id));
      const now = Date.now();
      const recent = this.mostUsedEntries()
        .filter((e) => !favoriteIds.has(e.item.id))
        .map((e) => ({ ...e.item, hint: relativeTime(e.last, now) }));
      if (recent.length) groups.push({ label: 'Abertos recentemente', items: recent, rank: 1000, layout: 'list' });
      groups.push({ label: 'Ações rápidas', items: this.actionItems().slice(0, 6), rank: 90, layout: 'tiles' });
      if (!this.favorites.length && !recent.length) {
        groups.push({ label: 'Ir para', items: this.pageItems().slice(0, 6), rank: 80, layout: 'list' });
      }
      return groups;
    }

    const groups: SearchGroup[] = [];
    this.pushMatched(groups, 'Ações', this.actionItems(), q);
    this.pushMatched(groups, 'Páginas', this.pageItems(), q);
    return groups.sort((a, b) => b.rank - a.rank);
  }

  // ── Entidades (assíncrono) — todas de uma vez, só os grupos não-vazios ────────────────────────
  searchDynamic(query: string): Observable<SearchGroup[]> {
    const q = query.trim();
    if (!q) return of([]);

    const streams: Observable<SearchGroup | null>[] = this.entitySources()
      .filter((s) => this.hasRoles(s.roles))
      .map((s) => this.cachedItems(s).pipe(map((items) => toGroup(s.label, scoreAndSort(items, q)))));

    streams.push(
      this.searchProducts(q),
      this.searchInvoices(q),
      this.searchOrders(q),
      this.searchAdvertisements(q),
      this.viewItems().pipe(map((items) => toGroup('Visões', scoreAndSort(items, q)))),
    );

    return forkJoin(streams).pipe(
      map((groups) => groups.filter((g): g is SearchGroup => g !== null).map((g) => this.boostGroup(g))),
    );
  }

  // ── Visões (padrão de cada tela + as salvas pelo usuário) ────────────────────────────────────
  /** Recarrega as visões salvas. Chamado ao abrir a busca. */
  refreshUserViews(): void {
    this.userViews$ = this.listViews.listAll().pipe(
      // Resposta fora do formato ou API sem o GET de todas as telas: só as visões padrão.
      map((views) => (Array.isArray(views) ? views : [])),
      catchError(() => of([] as ServerListViewWithKey[])),
      shareReplay(1),
    );
  }

  private viewItems(): Observable<SearchItem[]> {
    const screens = VIEW_SCREENS.filter((s) => this.hasRoles(s.roles));
    return this.userViews$.pipe(
      map((server) =>
        screens.flatMap((screen) => [
          ...screen.systemViews.map((v) => this.viewItem(screen, v.id, v.name, v.icon, v.filters, false)),
          ...server
            .filter((s) => s.listKey === screen.screenKey)
            .map((s) => viewFromServer(s.name, s.payload))
            .map((v) => this.viewItem(screen, v.id, v.name, v.icon, v.filters, true)),
        ]),
      ),
    );
  }

  private viewItem(
    screen: ViewScreen,
    viewId: string,
    name: string,
    icon: string | undefined,
    filters: Record<string, unknown>,
    mine: boolean,
  ): SearchItem {
    const id = `view:${screen.screenKey}:${viewId}`;
    this.viewFilters.set(id, { screenKey: screen.screenKey, filters });
    return {
      id,
      type: 'view',
      title: name,
      subtitle: `${screen.title} · ${mine ? 'Minha visão' : 'Visão padrão'}`,
      icon: `ti ${icon || 'ti-bookmark'}`,
      route: `${screen.url}?${VIEW_QUERY_PARAM}=${encodeURIComponent(viewId)}`,
      keywords: 'visão',
    };
  }

  /**
   * Antes de abrir uma visão: grava o recorte dela como o último da tela, para a primeira carga da
   * tela já sair com ele (a tela só acende o chip, ver ListViewsState). Visão que não passou pela
   * lista desta abertura (favorito, mais usado) abre pelo ?visao= e a tela aplica sozinha.
   */
  prepareLaunch(item: SearchItem): void {
    const view = this.viewFilters.get(item.id);
    if (view) saveListFilters(view.screenKey, view.filters);
  }

  // ── Mais usados (localStorage, por navegador) ────────────────────────────────────────────────
  /** Registra que o item foi aberto: conta usos e guarda quando foi o último. */
  recordUse(item: SearchItem): void {
    if (!item.route) return;
    try {
      const usage = this.readUsage();
      const now = Date.now();
      usage[item.id] = { item: storable(item), count: (usage[item.id]?.count ?? 0) + 1, last: now };
      const kept = Object.values(usage)
        .sort((a, b) => frecency(b, now) - frecency(a, now))
        .slice(0, USAGE_MAX);
      localStorage.setItem(USAGE_KEY, JSON.stringify(Object.fromEntries(kept.map((e) => [e.item.id, e]))));
    } catch {
      // localStorage indisponível: a busca segue sem o histórico de uso.
    }
  }

  /** Os mais abertos, pesando o quanto e o quão recente (frecência). */
  mostUsed(): SearchItem[] {
    return this.mostUsedEntries().map((e) => e.item);
  }

  private mostUsedEntries(): UsageEntry[] {
    const now = Date.now();
    return Object.values(this.readUsage())
      .sort((a, b) => frecency(b, now) - frecency(a, now))
      .slice(0, MOST_USED_MAX);
  }

  private readUsage(): Record<string, UsageEntry> {
    try {
      const raw = localStorage.getItem(USAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? (Object.fromEntries(Object.entries(parsed).filter(([, e]) => isUsageEntry(e))) as Record<string, UsageEntry>)
          : {};
      }
      // Primeira vez com o histórico novo: os "Recentes" antigos entram com um uso cada, na ordem.
      const now = Date.now();
      return Object.fromEntries(
        this.getRecents().map((r, i) => [r.id, { item: storable(r), count: 1, last: now - i * 1000 }]),
      );
    } catch {
      return {};
    }
  }

  // Item usado ou favorito sobe na busca: com relevância de texto parecida, o que a pessoa abre mais vem antes.
  private boostGroup(group: SearchGroup): SearchGroup {
    const items = this.boost(group.items);
    return { ...group, items, rank: bestScore(items) };
  }

  private boost(items: SearchItem[]): SearchItem[] {
    const usage = this.readUsage();
    const now = Date.now();
    return items
      .map((item) => {
        const entry = usage[item.id];
        const used = entry ? Math.min(25, frecency(entry, now) * 6) : 0;
        const favorite = this.isFavorite(item.id) ? 30 : 0;
        return used || favorite ? { ...item, score: (item.score ?? 0) + used + favorite } : item;
      })
      .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  }

  // ── Favoritos (servidor, por usuário e empresa) ──────────────────────────────────────────────
  /** Busca os favoritos da empresa ativa. Chamado ao abrir a busca. */
  loadFavorites(): void {
    this.listViews.list(FAVORITES_LIST_KEY).subscribe({
      next: (views) => {
        const stored = Array.isArray(views) ? views.find((v) => v.name === FAVORITES_NAME) : undefined;
        const items = (stored?.payload as { items?: unknown } | undefined)?.items;
        this.favorites = Array.isArray(items) ? items.filter(isStoredItem).slice(0, FAVORITES_MAX) : [];
        this.favoritesChanged.next();
      },
      // Sem resposta: fica o que já estava na tela.
      error: () => undefined,
    });
  }

  isFavorite(id: string): boolean {
    return this.favorites.some((f) => f.id === id);
  }

  /** Liga ou desliga a estrela. Muda na hora e grava em seguida (o alerta de erro é do serviço). */
  toggleFavorite(item: SearchItem): void {
    if (!item.route) return;
    this.favorites = this.isFavorite(item.id)
      ? this.favorites.filter((f) => f.id !== item.id)
      : [...this.favorites, storable(item)].slice(-FAVORITES_MAX);
    this.favoritesChanged.next();
    this.favoritesSave.next(this.favorites);
  }

  private cachedItems(source: EntitySource): Observable<SearchItem[]> {
    let cached = this.cache.get(source.label);
    if (!cached) {
      cached = source.load().pipe(
        map((rows) => rows.map((r) => source.toItem(r))),
        catchError(() => of([] as SearchItem[])),
        shareReplay(1),
      );
      this.cache.set(source.label, cached);
    }
    return cached;
  }

  // Produtos: a grade virou paginada no servidor (catálogo de 11 mil itens = 5 MB, que a busca
  // baixava inteiro na 1ª tecla) — mesma busca `search` da grade (SKU, nome, marca, EAN, NCM, id).
  private searchProducts(q: string): Observable<SearchGroup | null> {
    if (!this.hasRoles([KEYCLOAK_ROLES.PRODUCT])) return of(null);
    return this.products.list({ search: q, pageSize: ENTITY_RESULTS_MAX, sortBy: 'stock', descending: true }).pipe(
      map((page) =>
        toGroup(
          'Produtos',
          page.data.map((p: Product) => {
            return scoreSoft(q, {
              id: `product:${p.id}`,
              type: 'product',
              kind: 'product',
              title: p.name,
              subtitle: `SKU ${p.sku} · ${money(p.price)}`,
              icon: 'ti ti-package',
              route: `/produtos/editar/${p.id}`,
              ...productDetail(p),
            });
          }),
        ),
      ),
      catchError(() => of(null)),
    );
  }

  // Notas e pedidos: busca SERVER-SIDE (filtro `search`) — não carrega tudo. Server já casou, então
  // não dropamos por score (scoreSoft: destaque quando bate, piso quando não).
  private searchInvoices(q: string): Observable<SearchGroup | null> {
    if (!this.hasRoles([KEYCLOAK_ROLES.INVOICE])) return of(null);
    // includeFulfilled: quem digita o número de uma nota quer ACHAR a nota — inclusive a de pedido
    // Full, que a grid esconde por padrão.
    return this.invoices.list({ search: q, pageSize: ENTITY_RESULTS_MAX, descending: true, includeFulfilled: true }).pipe(
      map((page) =>
        toGroup(
          'Notas fiscais',
          page.data.map((i: InvoiceListItem) =>
            scoreSoft(q, {
              id: `invoice:${i.id}`,
              type: 'product',
              kind: 'invoice',
              title: i.number ? `NF ${i.number}${i.series ? '-' + i.series : ''}` : 'NF sem número',
              subtitle: `${i.recipientName || 'Destinatário não informado'} · ${money(i.totalAmount)}`,
              icon: 'ti ti-file-invoice',
              route: `/notas-fiscais/${i.id}`,
              ...invoiceDetail(i),
            }),
          ),
        ),
      ),
      catchError(() => of(null)),
    );
  }

  // Anúncios: a lista virou paginada no servidor (catálogos de milhares) — mesma busca `search`
  // da grade (nome, SKU, categoria e SKU de item), sem baixar tudo.
  private searchAdvertisements(q: string): Observable<SearchGroup | null> {
    if (!this.hasRoles([KEYCLOAK_ROLES.ADVERTISEMENT])) return of(null);
    // includeFulfilled: busca por nome/SKU/id quer ACHAR o anúncio — inclusive o Full.
    return this.advertisements.list({ search: q, pageSize: ENTITY_RESULTS_MAX, includeFulfilled: true }).pipe(
      map((page) =>
        toGroup(
          'Anúncios',
          page.data.map((a: AdvertisementListItem) =>
            scoreSoft(q, {
              id: `ad:${a.id}`,
              type: 'product',
              kind: 'ad',
              title: a.name,
              subtitle: `SKU ${a.sku}${a.categoryName ? ' · ' + a.categoryName : ''}`,
              icon: 'ti ti-speakerphone',
              route: `/anuncios/editar/${a.id}`,
              ...adDetail(a),
            }),
          ),
        ),
      ),
      catchError(() => of(null)),
    );
  }

  private searchOrders(q: string): Observable<SearchGroup | null> {
    if (!this.hasRoles([KEYCLOAK_ROLES.ORDER])) return of(null);
    // includeFulfilled: mesma razão das notas — busca por identificador não esconde nada.
    return this.orders.list({ search: q, pageSize: ENTITY_RESULTS_MAX, descending: true, includeFulfilled: true }).pipe(
      map((page) =>
        toGroup(
          'Pedidos',
          page.data.map((o: OrderListItem) =>
            scoreSoft(q, {
              id: `order:${o.id}`,
              type: 'product',
              kind: 'order',
              title: `Pedido ${o.externalOrderId}`,
              subtitle: `${providerLabel(o.provider)} · ${o.customerName || 'Cliente não informado'}`,
              icon: 'ti ti-receipt-2',
              route: `/pedidos/${o.id}`,
              ...orderDetail(o),
            }),
          ),
        ),
      ),
      catchError(() => of(null)),
    );
  }

  // Limpa o cache de uma fonte (ex.: após criar/editar) pra próxima busca refletir a mudança.
  invalidate(label?: string): void {
    if (label) this.cache.delete(label);
    else this.cache.clear();
  }

  private pushMatched(groups: SearchGroup[], label: string, items: SearchItem[], q: string): void {
    const matched = this.boost(scoreAndSort(items, q));
    if (matched.length) groups.push({ label, items: matched, rank: bestScore(matched) });
  }

  // ── Recentes (formato antigo, só leitura para a migração) ────────────────────────────────────
  private getRecents(): SearchItem[] {
    try {
      const raw = localStorage.getItem(RECENTS_KEY);
      const arr = raw ? (JSON.parse(raw) as unknown[]) : [];
      return Array.isArray(arr) ? arr.filter(isStoredItem) : [];
    } catch {
      return [];
    }
  }

  private hasRoles(roles: string[]): boolean {
    if (!roles || roles.length === 0) return true;
    return this.permissions.hasAny(roles);
  }
}

// ── Relevância (funções puras) ────────────────────────────────────────────────────────────────
function norm(s: string): string {
  return (s || '').toLowerCase().normalize('NFD').replace(new RegExp('[\\u0300-\\u036f]', 'g'), '');
}

// Melhor casamento contíguo de q em text. Prefixo (+40) > início de palavra (+24) > meio; penaliza
// posição. score < 0 = sem match.
function scoreText(q: string, text: string, base: number): { score: number; range: MatchRange | null } {
  const t = norm(text);
  const i = t.indexOf(q);
  if (i < 0) return { score: -1, range: null };
  let bonus = 0;
  if (i === 0) bonus = 40;
  else if (t[i - 1] === ' ' || t[i - 1] === '-' || t[i - 1] === '/') bonus = 24;
  return { score: base + bonus - Math.min(i, 12), range: { start: i, len: q.length } };
}

function scoreItem(q: string, item: SearchItem & { keywords?: string }): SearchItem | null {
  const nq = norm(q);
  if (!nq) return { ...item, score: 0, hlTitle: null, hlSubtitle: null };

  const inTitle = scoreText(nq, item.title, 100);
  const inSub = item.subtitle ? scoreText(nq, item.subtitle, 70) : { score: -1, range: null };
  const inKw = item.keywords ? scoreText(nq, item.keywords, 40) : { score: -1, range: null };

  const best = Math.max(inTitle.score, inSub.score, inKw.score);
  if (best < 0) return null;

  return { ...item, score: best, hlTitle: inTitle.range, hlSubtitle: inTitle.score < 0 ? inSub.range : null };
}

// Para itens JÁ filtrados pela origem (server-side): mantém mesmo sem casar no texto visível, com
// um piso de relevância; destaca quando houver match.
function scoreSoft(q: string, item: SearchItem): SearchItem {
  return scoreItem(q, item) ?? { ...item, score: 55, hlTitle: null, hlSubtitle: null };
}

function scoreAndSort(items: SearchItem[], q: string): SearchItem[] {
  return items
    .map((it) => scoreItem(q, it))
    .filter((it): it is SearchItem => it !== null)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
}

function toGroup(label: string, items: SearchItem[]): SearchGroup | null {
  const capped = items.slice(0, ENTITY_RESULTS_MAX);
  return capped.length ? { label, items: capped, rank: bestScore(capped) } : null;
}

function bestScore(items: SearchItem[]): number {
  return items.reduce((max, it) => Math.max(max, it.score ?? 0), 0);
}

// O que se guarda de um item (histórico de uso e favoritos): o necessário para mostrar e abrir.
function storable(item: SearchItem): SearchItem {
  return {
    id: item.id,
    type: item.type === 'recent' ? 'page' : item.type,
    kind: item.kind,
    title: item.title,
    subtitle: item.subtitle,
    icon: item.icon,
    route: item.route,
  };
}

function isStoredItem(value: unknown): value is SearchItem {
  const item = value as Partial<SearchItem> | null;
  return !!item && typeof item.id === 'string' && typeof item.title === 'string'
    && typeof item.icon === 'string' && typeof item.route === 'string';
}

function isUsageEntry(value: unknown): value is UsageEntry {
  const entry = value as Partial<UsageEntry> | null;
  return !!entry && isStoredItem(entry.item) && typeof entry.count === 'number' && typeof entry.last === 'number';
}

// Frecência: usos pesados pela idade do último. Hoje vale inteiro; na semana, 70%; no mês, 40%.
export function frecency(entry: Pick<UsageEntry, 'count' | 'last'>, now: number): number {
  const days = (now - entry.last) / 86_400_000;
  const weight = days < 1 ? 1 : days < 7 ? 0.7 : days < 30 ? 0.4 : 0.15;
  return entry.count * weight;
}

function money(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);
}
